import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import SearchableCountrySelect from './SearchableCountrySelect';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import styles from './FiltersSidebar.module.css';

function FiltersSidebar({
  providerType = 'hair',
  categories = [],
  services = [],
  selectedServices,
  selectedCities: initialSelectedCities = [],
  selectedCity = '',
  selectedCountry = '',
  onCountryChange,
  kidsAccepted: initialKidsAccepted = false,
  menAccepted: initialMenAccepted = false,
  canTravel: initialCanTravel = false,
  hasPhotos: initialHasPhotos = false,
  searchText = '',
  onSelectedCityChange,
  onApply,
  onClose,
  loadingCatalog,
  sortBy,
  sortOrder,
  onSortByChange,
  onSortOrderToggle,
  canSortByDistance,
  hasActiveFilters = false,
  onClearFilters,
}) {
  const { t, language } = useTranslation();
  const locale = language || 'fr';
  const [localState, setLocalState] = useState({
    selectedServices: selectedServices || [],
    selectedCities: initialSelectedCities || [],
    kidsAccepted: initialKidsAccepted,
    menAccepted: initialMenAccepted,
    canTravel: initialCanTravel,
    hasPhotos: initialHasPhotos,
  });
  const [expanded, setExpanded] = useState({});
  const [googlePlacesLoaded, setGooglePlacesLoaded] = useState(false);
  const cityInputRef = useRef(null);
  const cityAutocompleteRef = useRef(null);

  // Fonction pour appliquer les filtres immédiatement
  const applyFilters = useCallback((newState) => {
    onApply?.({
      selectedServices: newState.selectedServices,
      selectedCities: newState.selectedCities,
      kidsAccepted: newState.kidsAccepted,
      menAccepted: newState.menAccepted,
      canTravel: newState.canTravel,
      hasPhotos: newState.hasPhotos,
      // searchText est géré séparément pour éviter de le réinitialiser
    });
  }, [onApply]);

  useEffect(() => {
    setLocalState((prev) => ({
      ...prev,
      selectedServices: selectedServices || [],
      selectedCities: initialSelectedCities || [],
      kidsAccepted: initialKidsAccepted,
      menAccepted: initialMenAccepted,
      canTravel: initialCanTravel,
      hasPhotos: initialHasPhotos,
    }));
  }, [
    selectedServices,
    initialSelectedCities,
    initialKidsAccepted,
    initialMenAccepted,
    initialCanTravel,
    initialHasPhotos,
  ]);

  useEffect(() => {
    if (providerType === 'hair') {
      return;
    }
    setLocalState((prev) => {
      if (!prev.kidsAccepted && !prev.menAccepted) {
        return prev;
      }
      const next = { ...prev, kidsAccepted: false, menAccepted: false };
      applyFilters(next);
      return next;
    });
  }, [providerType, applyFilters]);

  // Charger Google Places API (langue = langue sélectionnée par l'utilisateur)
  useEffect(() => {
    loadGoogleMaps({ requirePlaces: true, language: locale || 'fr' })
      .then(() => {
        setGooglePlacesLoaded(true);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Impossible de charger Google Places pour la recherche de villes.', error);
      });
  }, [locale]);

  // Initialiser Google Maps Autocomplete pour la ville
  useEffect(() => {
    if (!googlePlacesLoaded || !cityInputRef.current) {
      return () => {};
    }

    // Détruire l'ancien autocomplete s'il existe
    if (cityAutocompleteRef.current) {
      window.google.maps.event.clearInstanceListeners(cityAutocompleteRef.current);
      cityAutocompleteRef.current = null;
    }

    // Vider l'input quand le pays change
    if (cityInputRef.current) {
      cityInputRef.current.value = '';
    }

    const countryCode = (selectedCountry || 'be').toLowerCase();
    cityAutocompleteRef.current = new window.google.maps.places.Autocomplete(cityInputRef.current, {
      types: ['(cities)'],
      componentRestrictions: { country: [countryCode] },
    });

    const handlePlaceChanged = () => {
      const place = cityAutocompleteRef.current?.getPlace();
      const cityComponent = place?.address_components?.find((component) =>
        component.types.some((type) =>
          ['locality', 'postal_town', 'administrative_area_level_1'].includes(type),
        ),
      );
      const label = cityComponent?.long_name || place?.name || cityInputRef.current.value;
      const cityValue = label?.trim() || '';
      
      if (cityValue && !localState.selectedCities.includes(cityValue)) {
        setLocalState((prev) => {
          const newState = {
          ...prev,
          selectedCities: [...prev.selectedCities, cityValue],
          };
          applyFilters(newState);
          return newState;
        });
      }

      if (cityValue) {
        onSelectedCityChange?.(cityValue);
      }
      
      // Réinitialiser le champ
      if (cityInputRef.current) {
        cityInputRef.current.value = '';
      }
    };

    cityAutocompleteRef.current.addListener('place_changed', handlePlaceChanged);

    return () => {
      if (cityAutocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(cityAutocompleteRef.current);
        cityAutocompleteRef.current = null;
      }
    };
  }, [googlePlacesLoaded, selectedCountry, localState.selectedCities, onSelectedCityChange, applyFilters]);

  const servicesByCategory = useMemo(() => {
    const normalizedSearchText = searchText?.toLowerCase().trim() || '';
    return categories.map((category) => {
      const categoryServices = services.filter((service) => service.categoryCode === category.categoryCode);
      const hasSelectedServices = categoryServices.some((service) => localState.selectedServices.includes(service.code));
      // Vérifier aussi si un service de la catégorie correspond au texte de recherche
      const hasSearchedService = normalizedSearchText && categoryServices.some((service) => 
        service.label?.toLowerCase().includes(normalizedSearchText)
      );
      // Traduire le label de la catégorie
      const categoryCode = category.categoryCode || category.code || '';
      const frenchCategoryLabel = category.label || '';
      // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
      let categoryLabel = frenchCategoryLabel;
      if (locale && locale !== 'fr' && category.en) {
        categoryLabel = category.en;
      } else {
        const translatedCategoryLabel = t(`categories.${categoryCode}`, { defaultValue: frenchCategoryLabel });
        categoryLabel = translatedCategoryLabel || frenchCategoryLabel;
      }
      // Traduire les services
      const translatedServices = categoryServices.map((service) => {
        const serviceCode = service.code || service.prestationCode || '';
        const frenchServiceLabel = service.label || service.prestation || '';
        // Utiliser le champ 'en' si la langue n'est pas français et si le champ existe
        let serviceLabel = frenchServiceLabel;
        if (locale && locale !== 'fr' && service.en) {
          serviceLabel = service.en;
        } else {
          const translatedServiceLabel = t(`services.${serviceCode}`, { defaultValue: frenchServiceLabel });
          serviceLabel = translatedServiceLabel || frenchServiceLabel;
        }
        return {
          ...service,
          label: serviceLabel,
        };
      });
      return {
        ...category,
        label: categoryLabel,
        services: translatedServices,
        hasSelectedServices: hasSelectedServices || hasSearchedService, // Bold si sélectionné OU recherché
      };
    });
  }, [categories, services, localState.selectedServices, searchText, t]);

  const toggleCategoryExpansion = (categoryCode) => {
    setExpanded((prev) => ({
      ...prev,
      [categoryCode]: !prev[categoryCode],
    }));
  };

  const toggleServiceSelection = (code) => {
    setLocalState((prev) => {
      const set = new Set(prev.selectedServices);
      if (set.has(code)) {
        set.delete(code);
      } else {
        set.add(code);
      }
      const newState = { ...prev, selectedServices: Array.from(set) };
      applyFilters(newState);
      return newState;
    });
  };

  const handleChange = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setLocalState((prev) => {
      const newState = { ...prev, [key]: value };
      applyFilters(newState);
      return newState;
    });
  };

  const handleRemoveCity = (cityToRemove) => {
    const updatedCities = localState.selectedCities.filter((city) => city !== cityToRemove);
    setLocalState((prev) => {
      const newState = {
      ...prev,
        selectedCities: updatedCities,
      };
      applyFilters(newState);
      return newState;
    });
    if (selectedCity === cityToRemove) {
      onSelectedCityChange?.(updatedCities[0] || '');
    }
  };

  const handleReset = () => {
    const resetState = {
      selectedServices: [],
      selectedCities: [],
      kidsAccepted: false,
      menAccepted: false,
      canTravel: false,
      hasPhotos: false,
    };
    setLocalState(resetState);
    onApply?.({
      ...resetState,
      searchText: '',
    });
    onSelectedCityChange?.('');
  };

  return (
    <div className={styles.sidebar}>
      {/* Bouton de fermeture en haut (croix) – mobile uniquement */}
      <button type="button" onClick={onClose} className={styles.topCloseBtn}>
        ✕
      </button>

      {/* Type de prestations */}
      <div className={styles.heading}>
        <h2>
          {t('search.refineSearch')}
          {hasActiveFilters ? (
            <span className={styles.activeFilterBadge} onClick={onClearFilters}>
              {t('search.activeFilter')} ×
            </span>
          ) : (
            <span className={styles.activeFilterBadge} style={{ visibility: 'hidden' }}>{t('search.activeFilter')} ×</span>
          )}
        </h2>
      </div>

      {/* Pays avec recherche (libellés dans la langue du site) — synchro avec le dropdown pays de la barre mobile */}
      <div className={styles.countryFilter}>
        <SearchableCountrySelect
          value={(selectedCountry || 'be').toUpperCase()}
          onChange={(code) => onCountryChange?.(code ? code.toLowerCase() : 'be')}
          locale={locale}
          placeholder={t('search.allCountries') || 'Tous les pays'}
          allowEmpty={false}
          noResultsLabel={t('search.noCountryFound') || 'Aucun pays trouvé'}
          inputClassName={styles.countrySelect}
        />
      </div>

      {/* Champ de recherche de ville */}
      <div className={styles.citySearch}>
        <div className={styles.cityInputWrapper}>
          <input
            ref={cityInputRef}
            type="text"
            placeholder={t('search.city')}
            className={styles.cityInput}
          />
        </div>
        {localState.selectedCities.length > 0 && (
          <div className={styles.cityChips}>
            {localState.selectedCities.map((city) => (
              <span key={city} className={styles.cityChip}>
                {city}
                <button
                  type="button"
                  onClick={() => handleRemoveCity(city)}
                  className={styles.cityChipRemove}
                  aria-label={t('search.removeCity', { city })}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Recherche par nom / pseudo de coiffeuse */}
      <div className={styles.nameSearch}>
        <div className={styles.nameInputWrapper}>
          <input
            type="text"
            value={searchText}
            onChange={(event) => {
              const value = event.target.value;
              // On conserve l'état local des autres filtres et on met à jour uniquement searchText
              onApply?.({
                ...localState,
                searchText: value,
              });
            }}
            placeholder={t('search.name')}
            className={styles.nameSearchInput}
          />
        </div>
      </div>

      {/* Infos générales - maintenant en premier */}
      <div className={styles.generalInfoSection}>
        {providerType === 'hair' && (
          <>
            <div className={styles.toggleRow}>
              <label className={styles.toggleItem}>
                <input
                  type="checkbox"
                  checked={localState.kidsAccepted}
                  onChange={handleChange('kidsAccepted')}
                />
                <span>{t('search.coiffesChildren')}</span>
              </label>
            </div>
            <div className={styles.toggleRow}>
              <label className={styles.toggleItem}>
                <input
                  type="checkbox"
                  checked={localState.menAccepted}
                  onChange={handleChange('menAccepted')}
                />
                <span>{t('search.coiffesMen')}</span>
              </label>
            </div>
          </>
        )}
        <div className={styles.toggleRow}>
          <label className={styles.toggleItem}>
            <input
              type="checkbox"
              checked={localState.canTravel}
              onChange={handleChange('canTravel')}
            />
            <span>{t('search.canTravelLabel')}</span>
          </label>
        </div>
        <div className={styles.toggleRow}>
          <label className={styles.toggleItem}>
            <input
              type="checkbox"
              checked={localState.hasPhotos}
              onChange={handleChange('hasPhotos')}
            />
            <span>{t('search.photosAvailableLabel')}</span>
          </label>
        </div>
      </div>

      {/* Tri des résultats */}
      <div className={styles.sortSection}>
        <div className={styles.sortHeader}>
          <h3>{t('search.sort')}</h3>
        </div>
        <div className={styles.sortRow}>
          <select
            id="filterSortSelect"
            value={sortBy}
            onChange={(e) => onSortByChange?.(e.target.value)}
            className={styles.sortSelect}
          >
            <option value="-">{t('search.sortDefault')}</option>
            <option value="distance" disabled={!canSortByDistance}>
              {t('search.sortDistance')}
            </option>
            <option value="rating">{t('search.sortRating')}</option>
            <option value="alphabetical">{t('search.sortAlphabetical')}</option>
            <option value="createdAt">{t('search.sortCreatedAt')}</option>
          </select>
          <button
            type="button"
            className={`${styles.sortOrderToggle} ${
              sortOrder === 'asc' ? styles.sortOrderAsc : styles.sortOrderDesc
            }`}
            onClick={() => onSortOrderToggle?.()}
            aria-label={sortOrder === 'asc' ? t('search.sortAscending') : t('search.sortDescending')}
          >
            <span className={styles.sortIcon}>{sortOrder === 'asc' ? 'crois.' : 'décr.'}</span>
          </button>
        </div>
      </div>

      {/* Type de prestations */}
      <div className={styles.heading}>
        <h3>{t('search.serviceTypes')}</h3>
      </div>

      <div className={styles.categories}>
        {loadingCatalog && <p className={styles.loading}>{t('search.loadingCategories')}</p>}
        {!loadingCatalog &&
          servicesByCategory.map((category) => (
            <div key={category.categoryCode} className={styles.category}>
              <div
                className={styles.categoryHeader}
                onClick={() => toggleCategoryExpansion(category.categoryCode)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCategoryExpansion(category.categoryCode);
                  }
                }}
              >
                <span className={`${styles.categoryLabel} ${category.hasSelectedServices ? styles.categoryLabelBold : ''}`}>
                  {category.label}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCategoryExpansion(category.categoryCode);
                  }}
                  className={styles.expandBtn}
                  aria-label={expanded[category.categoryCode] ? t('search.collapse') : t('search.expand')}
                >
                  {expanded[category.categoryCode] ? '▴' : '▾'}
                </button>
              </div>
              {expanded[category.categoryCode] && (
                <div className={styles.services}>
                  {category.services.map((service) => (
                    <label key={service.code} className={styles.serviceItem}>
                      <input
                        type="checkbox"
                        checked={localState.selectedServices.includes(service.code)}
                        onChange={() => toggleServiceSelection(service.code)}
                      />
                      <span>{service.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.validateBtn}
          onClick={onClose}
        >
          Valider
        </button>
        <button type="button" className={styles.resetBtn} onClick={handleReset}>
          {t('search.reset')}
        </button>
      </div>
    </div>
  );
}

export default FiltersSidebar;
